/**
 * /fr/documentation-produit — docs concept page (FR). Targets documentation
 * produit (40) / documentation utilisateur (50) / documentation logiciel (20).
 * Links /fr/prd-document-exigences-produit + /fr/comparisons/notion-ai.
 */
import { MarketingShell } from './MarketingShell';

export function FrDocumentationProduit() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/documentation-produit"
      hreflangLocales={['fr']}
      documentTitle="Documentation produit : guide, structure et outils · Draftlize"
      documentDescription="Comment structurer une documentation produit qui reste à jour : types de docs, modèle, et comment lier la doc aux décisions produit pour qu'elle ne dérive plus."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Documentation</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            La documentation produit,<br />
            <em>de A à Z.</em>
          </h1>
          <p className="lede">
            La documentation produit couvre tout ce qui explique <em>quoi</em> a été construit, <em>comment</em> et <em>pourquoi</em> : doc interne, doc technique, doc utilisateur. Bien faite, elle aligne l'équipe et fait gagner un temps fou. Le problème n'est pas de l'écrire — c'est qu'elle <em>ment six semaines plus tard</em>, quand le produit a bougé et que la doc, elle, est restée figée. Voici comment la structurer, et comment la garder vraie.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Les types de documentation produit.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Documentation interne</strong> — specs, décisions, architecture : pour l'équipe qui construit.</li>
            <li><strong>Documentation technique</strong> — API, modèle de données, intégrations : pour les développeurs.</li>
            <li><strong>Documentation utilisateur</strong> — guides, FAQ, tutoriels : pour ceux qui utilisent le produit.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Une source unique de vérité, une arborescence claire, un propriétaire par section. Mais la partie qui vieillit le plus vite, ce sont les <em>décisions</em> — la doc n'en est que la dérivée. Reliez la doc au <a href="/fr/prd-document-exigences-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a> et aux décisions qui la sous-tendent.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Le piège : une doc qui ment six semaines plus tard.</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Une page de doc est correcte le jour où on l'écrit. Puis une décision change, la page non — et personne ne le remarque. La doc figée devient un piège : on s'y fie alors qu'elle est fausse. La solution n'est pas plus de discipline, c'est un substrat qui signale ce qui devient périmé.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">La doc dérive des décisions</h3>
            <p>Dans Draftlize, les décisions sont des cartes ; la documentation en découle. Quand une décision change, la section de doc qui en dépend passe en « stale » au lieu de mentir en silence.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Détection de dérive</h3>
            <p>Comme un système de build suit les artefacts invalidés, le substrat suit ce que votre changement rend obsolète — vous savez quoi mettre à jour, et où.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Lisible par votre agent IA</h3>
            <p>La doc devient un contexte que Claude Code ou Cursor lisent via MCP — à jour ou signalé, pas un document figé au statut inconnu.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Le problème d'une documentation produit n'est pas de l'écrire. C'est qu'elle ment six semaines plus tard, et que personne ne le voit.
        </blockquote>
        <cite>Reliez la doc aux décisions, et elle signale d'elle-même ce qui est périmé. C'est Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Documentation produit vs documentation technique ?', a: 'La doc technique (API, modèle de données) est un sous-ensemble de la doc produit, orienté développeurs. La doc produit couvre aussi le « pourquoi » : décisions, périmètre, contexte.' },
            { q: 'Quel outil pour la documentation produit ?', a: 'Un wiki (Notion, Confluence) pour la prose ; mais pour que les décisions restent à jour, il faut les relier. Voir notre comparatif Notion.' },
            { q: 'Comment la garder à jour ?', a: 'En faisant dériver la doc des décisions plutôt qu\'en la recopiant. Quand une décision change, Draftlize marque « stale » la partie de doc concernée.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>
                {f.a}
                {i === 1 && (<>{' '}<a href="/fr/comparisons/notion-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Draftlize vs Notion</a>{'.'}</>)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Une doc produit toujours synchronisée <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Faites dériver votre documentation produit des décisions. Quand une décision change, la doc concernée se signale — fini la doc qui ment en silence.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
